import { PushTriggerOption } from '@sendbird/chat';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import type { UserMessage } from '@sendbird/chat/message';
import { sendbirdSelectors, useSendbirdStateContext } from '@sendbird/uikit-react';
import GroupChannelHandler from '@sendbird/uikit-react/handlers/GroupChannelHandler';
import { useEffect } from 'react';

import useCurrentChannel from '@/stores/currentChannelStore';
import useNotificationStore from '@/stores/notificationStore';

import useSendbirdApi from './useSendbirdApi';

const usePushNotification = () => {
  const globalStore = useSendbirdStateContext();

  const { sendbirdChat } = useSendbirdApi();
  const { currentChannel, setCurrentChannel } = useCurrentChannel();
  const getGroupChannel = sendbirdSelectors.getGetGroupChannel(globalStore);

  const notificationStore = useNotificationStore();

  const fireNotification = (channel: GroupChannel, message: UserMessage) => {
    // NOTICE ::: silent 기능이 firefox에서는 안됌.
    const newOption: NotificationOptions = {
      icon: message.sender.plainProfileUrl,
      body: message.message,
      silent: !notificationStore.soundOn,
    };

    const noti = new Notification(message.sender.nickname, newOption);

    noti.addEventListener('click', function (this: Notification, event) {
      event.preventDefault();

      setCurrentChannel(channel);
      window.focus();
      this.close();
    });
  };

  useEffect(() => {
    // 만약 이미 유저가 푸시 알림을 허용해놓지 않았다면,
    if (Notification.permission !== 'granted') {
      // Chrome - 유저에게 푸시 알림을 허용하겠냐고 물어보고, 허용하지 않으면 return!
      try {
        Notification.requestPermission().then((permission) => {
          if (permission !== 'granted') {
            return;
          }
        });
      } catch (error) {
        // Safari - 유저에게 푸시 알림을 허용하겠냐고 물어보고, 허용하지 않으면 return!
        if (error instanceof TypeError) {
          Notification.requestPermission().then((permission) => {
            if (permission !== 'granted') {
              return;
            }
          });
        } else {
          //   console.error(error);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (sendbirdChat?.groupChannel && notificationStore?.isAllowedNotification) {
      sendbirdChat?.groupChannel.addGroupChannelHandler(
        `${Math.random()}`,
        new GroupChannelHandler({
          onMessageReceived(channel, message) {
            // 현재 포커싱된 채널과 메세지 온 채널이 같고, 창이 포커스되어 있으면
            if (currentChannel?.url === channel.url && window?.document?.hasFocus()) {
              return;
            }

            getGroupChannel(channel.url)
              .then(async (groupChannel) => {
                const result = groupChannel.myPushTriggerOption;

                if (result !== PushTriggerOption.OFF) {
                  fireNotification(groupChannel, message as UserMessage);
                }
              })
              .catch(() => {
                // MARK: error handler
                // console.log('e ::: ', e);
              });
          },
        }),
      );
    }

    return () => {
      sendbirdChat?.groupChannel?.removeAllGroupChannelHandlers();
    };
  }, [
    sendbirdChat?.groupChannel,
    currentChannel,
    notificationStore?.isAllowedNotification,
    notificationStore?.soundOn,
  ]);

  if (!Notification) {
    return;
  }

  return { fireNotification };
};

export default usePushNotification;
